/**
 * One rider-wallet adjustment waiting for a second person (D737b, ADR-0150), drawn the same way on the passenger record's "Saldo Vello" card and on the market's
 * approvals screen: what it does (add or take credit, how much, why), who proposed it and when, the note, and the decision.
 *
 * Four eyes: the operator who proposed it never decides it (the server refuses `SELF_APPROVAL`; here the buttons are replaced by a sentence that says so), nor does the
 * rider decide about their own account, nor an operator without `rider_wallet:approve`. A rejection needs a reason, asked before anything is sent. Every refusal is worded
 * by the central vocabulary (`refusal.admin.rider_wallet`), never a code.
 */
import { type ReactElement, useState } from 'react';
import { type RiderWalletAdjustmentView } from '@vello/api-client';
import { Badge, Button, Field, RecordRow, Stack, Text, TextInput } from '@vello/web-theme';
import { maySelfApprove, riderWalletApproveBlock, riderWalletDecisionIssue, withoutClosingStop } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { PersonName } from './person-name';
import { usePersonFormat } from './person-format';

export function RiderWalletPendingItem({
  adjustment: a,
  mayApprove,
  onDecided,
  showRider = false,
}: {
  adjustment: RiderWalletAdjustmentView;
  mayApprove: boolean;
  /** Called after a decision the server accepted, or after a refusal that means the list is stale (so the caller reads it again). */
  onDecided: (outcome: 'APPROVED' | 'REJECTED' | 'REFUSED') => void;
  /** The approvals screen names the rider; on the rider's own card it would be redundant. */
  showRider?: boolean;
}): ReactElement {
  const { runtime, me, grants } = useAdmin();
  const { t } = useT();
  const { v, fail, refusal } = useVocab();
  const fmt = usePersonFormat();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const block = riderWalletApproveBlock(a, me?.id, mayApprove, maySelfApprove(grants)); // D868: a super administrator decides their own proposal
  const reasonIssue = rejecting ? riderWalletDecisionIssue('REJECT', reason) : undefined;

  const decide = async (decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    setBusy(true);
    setError(undefined);
    const res = await runtime.riderWallet.decide(a.adjustmentId, { marketId: a.marketId, decision, ...(decision === 'REJECT' ? { reason: reason.trim() } : {}) });
    setBusy(false);
    if (!res.ok) {
      setError(t('rwallet.error', { reason: withoutClosingStop(refusal('refusal.admin.rider_wallet', res.error) ?? fail(res.error)) }));
      // The list may be stale (decided meanwhile, or the credit fell): read it again.
      if (res.error.status === 409) onDecided('REFUSED');
      return;
    }
    onDecided(decision === 'APPROVE' ? 'APPROVED' : 'REJECTED');
  };

  const blockText = block === 'OWN_PROPOSAL' ? t('rwallet.decision.own') : block === 'OWN_ACCOUNT' ? t('rwallet.decision.ownAccount') : block === 'NO_PERMISSION' ? t('rwallet.decision.noPermission') : undefined;

  return (
    <Stack gap={2} data-testid={`rwallet-pending-${a.adjustmentId}`}>
      <RecordRow dense>
        <Badge tone="warning">{v('rider_wallet.adjustment_status', a.status)}</Badge>
        <Text mono tone={a.direction === 'CREDIT' ? 'success' : 'danger'}>
          {fmt.money({ amountMinor: a.amountMinor.toString(), currency: a.currency })}
        </Text>
      </RecordRow>
      <Text as="p" role="caption">
        {t('rwallet.pending.change', { direction: v('rider_wallet.direction', a.direction), reason: v('rider_wallet.reason', a.reasonCode) })}
      </Text>
      {showRider && (
        <Text as="p" role="caption" tone="secondary">
          <PersonName id={a.riderId} />
        </Text>
      )}
      <Text as="p" role="caption" tone="secondary">
        {t('rwallet.pending.by', { when: fmt.when(a.proposedAt) })} <PersonName id={a.proposedBy} />
      </Text>
      <Text as="p" role="caption" tone="secondary">
        {a.note}
      </Text>
      {block === undefined &&
        (rejecting ? (
          <Stack gap={2}>
            <Field label={t('rwallet.reject.reason')} htmlFor={`rwallet-reject-${a.adjustmentId}`}>
              <TextInput id={`rwallet-reject-${a.adjustmentId}`} value={reason} maxLength={500} disabled={busy} onChange={(ev) => setReason(ev.target.value)} data-testid={`rwallet-reject-reason-${a.adjustmentId}`} />
            </Field>
            <RecordRow dense>
              <Button variant="danger" size="sm" disabled={busy || reasonIssue !== undefined} onClick={() => void decide('REJECT')} data-testid={`rwallet-reject-confirm-${a.adjustmentId}`}>
                {t('rwallet.reject.confirm')}
              </Button>
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => setRejecting(false)}>
                {t('ui.cancel')}
              </Button>
            </RecordRow>
          </Stack>
        ) : (
          <RecordRow dense>
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => void decide('APPROVE')} data-testid={`rwallet-approve-${a.adjustmentId}`}>
              {t('rwallet.approve')}
            </Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setRejecting(true)} data-testid={`rwallet-reject-${a.adjustmentId}`}>
              {t('rwallet.reject')}
            </Button>
          </RecordRow>
        ))}
      {blockText !== undefined && (
        <Text as="p" role="caption" tone="secondary" data-testid={`rwallet-block-${a.adjustmentId}`}>
          {blockText}
        </Text>
      )}
      {error !== undefined && (
        <Text as="p" role="caption" tone="danger" data-testid={`rwallet-error-${a.adjustmentId}`}>
          {error}
        </Text>
      )}
    </Stack>
  );
}
